import type { PillarPage } from "./content-types";

/**
 * The 3 feature pages (pillar-style), one per buying criterion:
 * OCR accuracy, secure cloud storage, categorization & filters.
 * Rendered by ContentPage at their FR and EN routes, prerendered like pillars.
 */

const FR = {
  ocr: "/fonctionnalites/precision-ocr",
  cloud: "/fonctionnalites/stockage-cloud-securise",
  filters: "/fonctionnalites/categorisation-filtres",
  receipts: "/application-recus",
  guide: "/blog/comment-choisir-application-recus",
  blogFaded: "/blog/recu-efface-quoi-faire",
  returns: "/application-retours-magasin",
};

const EN = {
  ocr: "/en/features/ocr-accuracy",
  cloud: "/en/features/secure-cloud-storage",
  filters: "/en/features/categories-filters",
  receipts: "/en/receipt-app",
  guide: "/en/blog/how-to-choose-receipt-app",
  blogFaded: "/en/blog/faded-receipt-what-to-do",
  returns: "/en/returns-app",
};

const SRC = {
  aimultiple: "https://aimultiple.com/ocr-accuracy",
  nanonets: "https://nanonets.com/blog/identifying-the-best-ocr-api/",
  gemini: "https://blog.google/innovation-and-ai/technology/developers-tools/gemini-3-pro-vision/",
};

export const features: PillarPage[] = [
  /* ============================ 1. OCR ACCURACY ============================ */
  {
    key: "ocr",
    frPath: FR.ocr,
    enPath: EN.ocr,
    image: "/og-base.png",
    imageAltFr: "Lecture OCR d'un reçu dans l'application ReFound",
    imageAltEn: "OCR reading of a receipt in the ReFound app",
    fr: {
      metaTitle: "Précision OCR : une application de reçus fiable | ReFound",
      metaDescription:
        "ReFound lit vos reçus avec Google Vision, au sommet des benchmarks OCR indépendants (98 % de précision), et Gemini pour les reçus difficiles à lire.",
      keywords:
        "application reçus OCR, précision OCR reçus, scanner reçu OCR, lecture automatique reçu, extraction données reçu, Google Vision reçu, reçu effacé OCR, ReFound",
      breadcrumb: "Précision OCR",
      eyebrow: "Fonctionnalité",
      h1: "Précision OCR : des reçus lus correctement, du premier coup",
      intro: [
        "L'OCR (reconnaissance optique de caractères), c'est la technologie qui transforme la photo d'un reçu en données : marchand, date, total, articles. Si cette lecture se trompe, tout le reste s'écroule. Un montant erroné ou une date mal lue, c'est une preuve d'achat qu'on ne peut plus utiliser.",
        "ReFound est propulsé par Google Vision, classé au sommet des benchmarks OCR indépendants (98 % de précision), et par le modèle Gemini le plus avancé de Google pour les reçus difficiles à lire. Résultat : des données fiables, même quand le papier ne l'est plus.",
      ],
      sections: [
        {
          heading: "Pourquoi la précision OCR est le critère numéro un",
          body: [
            "Une erreur de lecture est une erreur silencieuse : vous ne la remarquez pas au moment du scan, mais elle refait surface au pire moment — au comptoir des retours, ou pendant une réclamation de garantie. Une application de reçus ne vaut que ce que vaut sa lecture.",
            "C'est pourquoi la précision OCR est le premier des [3 critères essentiels pour choisir une application de reçus](/blog/comment-choisir-application-recus), avant même les fonctionnalités visibles.",
          ],
        },
        {
          heading: "Des benchmarks indépendants, pas des promesses",
          body: [
            "Plutôt que de développer un OCR maison, ReFound s'appuie sur les technologies de lecture les mieux évaluées par des tiers indépendants :",
          ],
          bullets: [
            "Le benchmark indépendant [AIMultiple](https://aimultiple.com/ocr-accuracy) mesure 98 % de précision de texte pour Google Cloud Vision, sur un banc d'essai qui inclut des reçus et des factures.",
            "Le benchmark d'APIs OCR de [Nanonets](https://nanonets.com/blog/identifying-the-best-ocr-api/), mené sur SROIE, un jeu de données de reçus numérisés, classe Google Cloud Vision meilleur choix global pour l'équilibre entre précision et vitesse.",
            "Google positionne [Gemini 3 Pro comme un leader en compréhension de documents](https://blog.google/innovation-and-ai/technology/developers-tools/gemini-3-pro-vision/) : documents en mauvais état, écriture manuscrite et mises en page complexes.",
          ],
        },
        {
          heading: "Reçus effacés, froissés ou interminables",
          body: [
            "Les reçus de la vraie vie ne sont pas des documents parfaits : papier thermique pâli, reçu plié au fond d'une poche, facture d'épicerie de 60 lignes. Quand un reçu est difficile à lire, ReFound fait appel au modèle Gemini le plus avancé de Google, conçu pour comprendre les documents en mauvais état et les mises en page complexes.",
            "Si votre reçu a déjà commencé à pâlir, voyez [quoi faire avec un reçu effacé](/blog/recu-efface-quoi-faire) — et scannez les prochains dès l'achat.",
          ],
        },
        {
          heading: "Tout est extrait automatiquement",
          body: [
            "Pas de saisie manuelle : ReFound extrait automatiquement les informations clés de chaque reçu.",
          ],
          bullets: [
            "Le marchand",
            "La date d'achat",
            "Le total",
            "Les taxes",
            "La carte de paiement utilisée",
            "Les articles, ligne par ligne",
          ],
        },
        {
          heading: "Au-delà de l'OCR : des articles enrichis",
          body: [
            "Lire le texte, c'est la base. ReFound va plus loin : les codes obscurs des magasins (les fameux SKU comme « 054-3178-6 ») sont transformés en vrais noms de produits accompagnés d'images, quand l'information est disponible. Vous savez ce que contient chaque reçu en un coup d'œil, et la [recherche par nom de produit](/fonctionnalites/categorisation-filtres) devient possible.",
            "C'est ce qui distingue une [application de reçus intelligents](/application-recus) d'un simple scanner. Un exemple concret : sur un [reçu Canadian Tire](/marchands/recus-canadian-tire) de notre compte de démonstration, la ligne « HLT NEO WK 4LB » est identifiée comme un WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights — un OCR parfait aurait rendu la même chaîne illisible.",
          ],
        },
      ],
      faqTitle: "Questions fréquentes sur la précision OCR",
      faq: [
        {
          q: "Quelle application de reçus a le meilleur OCR ?",
          a: "ReFound est propulsé par Google Vision, mesuré à 98 % de précision de texte par le benchmark indépendant AIMultiple et classé meilleur choix global par le benchmark de Nanonets sur le jeu de données de reçus SROIE. Pour les reçus difficiles à lire, ReFound utilise en plus le modèle Gemini le plus avancé de Google.",
        },
        {
          q: "Qu'est-ce que l'OCR exactement ?",
          a: "L'OCR (reconnaissance optique de caractères) est la technologie qui lit le texte sur une image. Pour un reçu, elle transforme la photo en données utilisables : marchand, date, total, taxes, carte de paiement et articles.",
        },
        {
          q: "Est-ce que ReFound peut lire un reçu effacé ou froissé ?",
          a: "Quand un reçu est difficile à lire, ReFound fait appel au modèle Gemini le plus avancé de Google, conçu pour les documents en mauvais état et les mises en page complexes. Le mieux reste de scanner le reçu tôt, avant que le papier thermique ne pâlisse trop.",
        },
        {
          q: "Quelles informations sont extraites de mes reçus ?",
          a: "ReFound extrait automatiquement le marchand, la date, le total, les taxes, la carte de paiement et les articles ligne par ligne. Quand l'information est disponible, les articles sont aussi enrichis avec de vrais noms de produits et des images.",
        },
        {
          q: "Dois-je corriger les données après la numérisation ?",
          a: "La lecture est très fiable grâce à Google Vision et Gemini, mais vous gardez toujours le contrôle : vous pouvez vérifier et ajuster les informations d'un reçu au besoin.",
        },
      ],
      ctaTitle: "Des reçus lus correctement, dès le premier scan",
      ctaBody:
        "ReFound lit vos reçus avec Google Vision et Gemini, puis les enrichit avec de vrais noms de produits. Scannez un reçu et voyez la différence.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Comment choisir une application de reçus", href: FR.guide, desc: "Les 3 critères essentiels, avec comparatif." },
        { label: "Stockage cloud sécurisé", href: FR.cloud, desc: "Vos reçus survivent à votre téléphone." },
        { label: "Catégorisation et filtres", href: FR.filters, desc: "Retrouvez n'importe quel reçu en secondes." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
      ],
    },
    en: {
      metaTitle: "OCR Accuracy: A Receipt App You Can Trust | ReFound",
      metaDescription:
        "ReFound reads your receipts with Google Vision, top-ranked in independent OCR benchmarks (98% accuracy), plus Gemini for hard-to-read receipts.",
      keywords:
        "receipt app OCR, OCR accuracy receipts, receipt scanner OCR, automatic receipt reading, receipt data extraction, Google Vision receipt, faded receipt OCR, ReFound",
      breadcrumb: "OCR accuracy",
      eyebrow: "Feature",
      h1: "OCR accuracy: receipts read correctly, the first time",
      intro: [
        "OCR (optical character recognition) is the technology that turns a photo of a receipt into data: merchant, date, total, items. If that reading is wrong, everything else falls apart. A misread amount or date is proof of purchase you can no longer trust.",
        "ReFound is powered by Google Vision, top-ranked in independent OCR benchmarks (98% text accuracy), plus Google's most advanced Gemini model for hard-to-read receipts. The result: reliable data, even when the paper no longer is.",
      ],
      sections: [
        {
          heading: "Why OCR accuracy is criterion number one",
          body: [
            "A reading error is a silent error: you do not notice it when you scan, but it resurfaces at the worst moment — at the return counter, or during a warranty claim. A receipt app is only as good as its reading.",
            "That is why OCR accuracy is the first of the [3 key criteria for choosing a receipt app](/en/blog/how-to-choose-receipt-app), before any visible feature.",
          ],
        },
        {
          heading: "Independent benchmarks, not promises",
          body: [
            "Rather than building a home-grown OCR, ReFound relies on the reading technologies best rated by independent third parties:",
          ],
          bullets: [
            "The independent [AIMultiple benchmark](https://aimultiple.com/ocr-accuracy) measures 98% text accuracy for Google Cloud Vision, on a test set that includes receipts and invoices.",
            "The [Nanonets OCR API benchmark](https://nanonets.com/blog/identifying-the-best-ocr-api/), run on SROIE, a scanned-receipt dataset, ranks Google Cloud Vision as the best overall performer balancing accuracy and speed.",
            "Google positions [Gemini 3 Pro as a leader in document understanding](https://blog.google/innovation-and-ai/technology/developers-tools/gemini-3-pro-vision/): messy documents, handwriting and complex layouts.",
          ],
        },
        {
          heading: "Faded, crumpled or extra-long receipts",
          body: [
            "Real-life receipts are not perfect documents: faded thermal paper, a receipt folded at the bottom of a pocket, a 60-line grocery bill. When a receipt is hard to read, ReFound calls on Google's most advanced Gemini model, built to understand messy documents and complex layouts.",
            "If your receipt has already started to fade, see [what to do with a faded receipt](/en/blog/faded-receipt-what-to-do) — and scan the next ones at purchase.",
          ],
        },
        {
          heading: "Everything is extracted automatically",
          body: [
            "No manual entry: ReFound automatically extracts the key information from every receipt.",
          ],
          bullets: [
            "The merchant",
            "The purchase date",
            "The total",
            "The taxes",
            "The payment card used",
            "The items, line by line",
          ],
        },
        {
          heading: "Beyond OCR: enriched items",
          body: [
            "Reading the text is the baseline. ReFound goes further: cryptic store codes (the famous SKUs like \"054-3178-6\") are turned into real product names with images, when the information is available. You know what is on each receipt at a glance, and [search by real product name](/en/features/categories-filters) becomes possible.",
            "That is what separates a [smart receipt app](/en/receipt-app) from a basic scanner. A concrete example: on a [Canadian Tire receipt](/en/merchants/canadian-tire-receipts) from our demo account, the line \"HLT NEO WK 4LB\" is identified as a WA:KE Athletic Neoprene Hexagonal Dumbbell, Single, Assorted Weights — perfect OCR would still have left that string unreadable.",
          ],
        },
      ],
      faqTitle: "OCR accuracy FAQ",
      faq: [
        {
          q: "Which receipt app has the best OCR?",
          a: "ReFound is powered by Google Vision, measured at 98% text accuracy by the independent AIMultiple benchmark and ranked best overall performer by the Nanonets benchmark on the SROIE scanned-receipt dataset. For hard-to-read receipts, ReFound also uses Google's most advanced Gemini model.",
        },
        {
          q: "What exactly is OCR?",
          a: "OCR (optical character recognition) is the technology that reads text from an image. For a receipt, it turns the photo into usable data: merchant, date, total, taxes, payment card and items.",
        },
        {
          q: "Can ReFound read a faded or crumpled receipt?",
          a: "When a receipt is hard to read, ReFound calls on Google's most advanced Gemini model, built for messy documents and complex layouts. The safest option is still to scan the receipt early, before the thermal paper fades too much.",
        },
        {
          q: "What information is extracted from my receipts?",
          a: "ReFound automatically extracts the merchant, date, total, taxes, payment card and items line by line. When the information is available, items are also enriched with real product names and images.",
        },
        {
          q: "Do I need to correct the data after scanning?",
          a: "Reading is highly reliable thanks to Google Vision and Gemini, but you always stay in control: you can review and adjust a receipt's information if needed.",
        },
      ],
      ctaTitle: "Receipts read correctly, from the first scan",
      ctaBody:
        "ReFound reads your receipts with Google Vision and Gemini, then enriches them with real product names. Scan a receipt and see the difference.",
      relatedTitle: "Read next",
      related: [
        { label: "How to choose a receipt app", href: EN.guide, desc: "The 3 key criteria, with a comparison table." },
        { label: "Secure cloud storage", href: EN.cloud, desc: "Your receipts outlive your phone." },
        { label: "Categories and filters", href: EN.filters, desc: "Find any receipt in seconds." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan, enrich and find your receipts." },
      ],
    },
  },

  /* ============================ 2. SECURE CLOUD ============================ */
  {
    key: "cloud",
    frPath: FR.cloud,
    enPath: EN.cloud,
    image: "/og-base.png",
    imageAltFr: "Reçus sauvegardés de façon sécurisée dans le cloud avec ReFound",
    imageAltEn: "Receipts safely backed up to the cloud with ReFound",
    fr: {
      metaTitle: "Stockage cloud sécurisé de vos reçus au Canada | ReFound",
      metaDescription:
        "Vos reçus restent privés et protégés. Ils sont conservés sur votre appareil, avec la possibilité d'activer la sauvegarde cloud. Base de données principale hébergée au Canada.",
      keywords:
        "app reçus stockage cloud sécurisé Canada, sauvegarde reçus, reçus cloud, reçus privés, hébergé au Canada, téléphone perdu reçus, ReFound",
      breadcrumb: "Stockage cloud sécurisé",
      eyebrow: "Fonctionnalité",
      h1: "Un stockage cloud sécurisé : vos reçus survivent à votre téléphone",
      intro: [
        "Un téléphone perdu, brisé ou volé ne devrait jamais emporter vos preuves d'achat avec lui. Vos reçus sont conservés sur votre appareil, avec la possibilité d'activer la sauvegarde cloud pour les retrouver en cas de besoin.",
        "Vos reçus restent privés et protégés. Lorsque la sauvegarde cloud est activée, ils sont conservés de façon sécurisée pour que vous puissiez les retrouver lorsque vous en avez besoin. La base de données principale de ReFound est hébergée au Canada.",
      ],
      sections: [
        {
          heading: "Un téléphone perdu ≠ des reçus perdus",
          body: [
            "Si vos reçus vivent uniquement dans la galerie photo de votre téléphone, ils disparaissent avec lui. C'est le piège des photos et des scanners locaux : la copie existe, mais à un seul endroit fragile.",
            "Avec ReFound, activez la sauvegarde cloud et vos reçus sont conservés en lieu sûr. Changez de téléphone, échappez-le dans le lac, faites-le réparer : reconnectez-vous et tout est là.",
          ],
        },
        {
          heading: "Une base de données hébergée au Canada",
          body: [
            "La base de données principale de ReFound est hébergée au Canada. C'est un point qui compte pour beaucoup de consommateurs canadiens.",
          ],
        },
        {
          heading: "Vos reçus restent privés",
          body: [
            "Les images sauvegardées dans le cloud sont conservées dans un stockage privé et ne sont pas publiquement accessibles. Et fidèle à sa politique, ReFound ne revend jamais vos données d'achat — vos reçus ne regardent que vous.",
          ],
          bullets: [
            "Des reçus privés et protégés",
            "La sauvegarde cloud s'active quand vous le voulez",
            "Aucune revente de vos données d'achat",
          ],
        },
        {
          heading: "Vos reçus sur tous vos appareils",
          body: [
            "Avec la sauvegarde cloud activée, vos reçus vous suivent : nouveau téléphone, appareil de remplacement ou second appareil du foyer. Le bon reçu est accessible au bon moment, sur vos appareils. Si vous le souhaitez, vous pouvez aussi choisir de partager un reçu grâce au [partage familial](/application-recus-famille).",
          ],
        },
        {
          heading: "Le deuxième des 3 critères essentiels",
          body: [
            "Le stockage sécurisé est le deuxième des [3 critères pour choisir une application de reçus](/blog/comment-choisir-application-recus), avec la [précision OCR](/fonctionnalites/precision-ocr) et la [catégorisation et les filtres](/fonctionnalites/categorisation-filtres). Une lecture parfaite ne sert à rien si la copie disparaît avec le téléphone.",
          ],
        },
      ],
      faqTitle: "Questions fréquentes sur le stockage sécurisé",
      faq: [
        {
          q: "Mes reçus sont-ils sauvegardés si je perds mon téléphone ?",
          a: "Oui, lorsque la sauvegarde cloud est activée. Vos reçus sont alors conservés de façon sécurisée : si votre téléphone est perdu, brisé ou volé, reconnectez-vous depuis un autre appareil et vos reçus sont là.",
        },
        {
          q: "Où sont stockés mes reçus ?",
          a: "Vos reçus sont conservés sur votre appareil, avec la possibilité d'activer la sauvegarde cloud pour les retrouver en cas de besoin. La base de données principale de ReFound est hébergée au Canada.",
        },
        {
          q: "Mes reçus sont-ils privés ?",
          a: "Oui. Vos reçus restent privés et protégés. Les images sauvegardées dans le cloud sont conservées dans un stockage privé et ne sont pas publiquement accessibles.",
        },
        {
          q: "Est-ce que ReFound revend mes données d'achat ?",
          a: "Non. ReFound applique une politique stricte : vos données d'achat ne sont jamais vendues, louées ou partagées avec des tiers.",
        },
        {
          q: "Puis-je accéder à mes reçus depuis un autre appareil ?",
          a: "Oui, lorsque la sauvegarde cloud est activée. Vous retrouvez vos reçus en vous connectant à votre compte depuis un autre téléphone (iPhone ou Android).",
        },
      ],
      ctaTitle: "Vos preuves d'achat, à l'abri",
      ctaBody:
        "Vos reçus restent privés et protégés, et la sauvegarde cloud vous permet de les retrouver en cas de besoin. Un téléphone perdu ne sera plus jamais une preuve d'achat perdue.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Comment choisir une application de reçus", href: FR.guide, desc: "Les 3 critères essentiels, avec comparatif." },
        { label: "Précision OCR", href: FR.ocr, desc: "Des reçus lus correctement, du premier coup." },
        { label: "Catégorisation et filtres", href: FR.filters, desc: "Retrouvez n'importe quel reçu en secondes." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
      ],
    },
    en: {
      metaTitle: "Secure Cloud Receipt Storage in Canada | ReFound",
      metaDescription:
        "Your receipts stay private and protected. They're kept on your device, with the option to turn on cloud backup. ReFound's main database is hosted in Canada.",
      keywords:
        "receipt app secure cloud storage Canada, receipt backup, cloud receipts, private receipts, hosted in Canada, lost phone receipts, ReFound",
      breadcrumb: "Secure cloud storage",
      eyebrow: "Feature",
      h1: "Secure cloud storage: your receipts outlive your phone",
      intro: [
        "A lost, broken or stolen phone should never take your proof of purchase with it. Your receipts are kept on your device, with the option to turn on cloud backup so you can find them when you need them.",
        "Your receipts stay private and protected. When cloud backup is on, they're kept securely so you can find them when you need them. ReFound's main database is hosted in Canada.",
      ],
      sections: [
        {
          heading: "A lost phone ≠ lost receipts",
          body: [
            "If your receipts live only in your phone's camera roll, they disappear with it. That is the trap of photos and local scanners: the copy exists, but in a single fragile place.",
            "With ReFound, turn on cloud backup and your receipts are kept safe. Switch phones, drop yours in the lake, send it for repair: sign back in and everything is there.",
          ],
        },
        {
          heading: "A database hosted in Canada",
          body: [
            "ReFound's main database is hosted in Canada. That matters to many Canadian consumers.",
          ],
        },
        {
          heading: "Your receipts stay private",
          body: [
            "Images backed up to the cloud are kept in private storage and are not publicly accessible. And true to its policy, ReFound never sells your purchase data — your receipts are nobody's business but yours.",
          ],
          bullets: [
            "Receipts that stay private and protected",
            "Cloud backup turns on whenever you want",
            "No resale of your purchase data",
          ],
        },
        {
          heading: "Your receipts on all your devices",
          body: [
            "With cloud backup on, your receipts follow you: a new phone, a replacement device or a second device in the household. The right receipt is available at the right time, on your devices. If you want to, you can also choose to share a receipt with [family sharing](/en/family-receipt-app).",
          ],
        },
        {
          heading: "The second of the 3 key criteria",
          body: [
            "Secure storage is the second of the [3 criteria for choosing a receipt app](/en/blog/how-to-choose-receipt-app), alongside [OCR accuracy](/en/features/ocr-accuracy) and [categories and filters](/en/features/categories-filters). A perfect reading is useless if the copy disappears with the phone.",
          ],
        },
      ],
      faqTitle: "Secure storage FAQ",
      faq: [
        {
          q: "Are my receipts backed up if I lose my phone?",
          a: "Yes, when cloud backup is on. Your receipts are then kept securely: if your phone is lost, broken or stolen, sign in from another device and your receipts are there.",
        },
        {
          q: "Where are my receipts stored?",
          a: "Your receipts are kept on your device, with the option to turn on cloud backup so you can find them when you need them. ReFound's main database is hosted in Canada.",
        },
        {
          q: "Are my receipts private?",
          a: "Yes. Your receipts stay private and protected. Images backed up to the cloud are kept in private storage and are not publicly accessible.",
        },
        {
          q: "Does ReFound sell my purchase data?",
          a: "No. ReFound applies a strict policy: your purchase data is never sold, rented or shared with third parties.",
        },
        {
          q: "Can I access my receipts from another device?",
          a: "Yes, when cloud backup is on. You get your receipts back by signing in to your account from another phone (iPhone or Android).",
        },
      ],
      ctaTitle: "Your proof of purchase, kept safe",
      ctaBody:
        "Your receipts stay private and protected, and cloud backup lets you find them when you need them. A lost phone will never again mean lost proof of purchase.",
      relatedTitle: "Read next",
      related: [
        { label: "How to choose a receipt app", href: EN.guide, desc: "The 3 key criteria, with a comparison table." },
        { label: "OCR accuracy", href: EN.ocr, desc: "Receipts read correctly, the first time." },
        { label: "Categories and filters", href: EN.filters, desc: "Find any receipt in seconds." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan, enrich and find your receipts." },
      ],
    },
  },

  /* ======================== 3. CATEGORIES & FILTERS ======================== */
  {
    key: "filters",
    frPath: FR.filters,
    enPath: EN.filters,
    image: "/og-base.png",
    imageAltFr: "Catégories et filtres de reçus dans l'application ReFound",
    imageAltEn: "Receipt categories and filters in the ReFound app",
    fr: {
      metaTitle: "Application pour catégoriser et filtrer ses reçus | ReFound",
      metaDescription:
        "ReFound classe vos reçus automatiquement et les filtre par magasin, carte de paiement ou montant. Recherchez même par vrai nom de produit, pas par code SKU.",
      keywords:
        "application catégoriser reçus filtres, catégories de reçus, filtrer reçus par magasin, filtrer reçus par carte, recherche reçu par produit, organiser reçus, ReFound",
      breadcrumb: "Catégorisation et filtres",
      eyebrow: "Fonctionnalité",
      h1: "Catégories et filtres : retrouvez n'importe quel reçu en quelques secondes",
      intro: [
        "Garder ses reçus, c'est bien. Les retrouver, c'est tout l'enjeu. Un reçu qu'on ne retrouve pas au moment d'un retour ou d'une réclamation ne vaut pas mieux qu'un reçu perdu.",
        "ReFound classe vos reçus automatiquement, vous laisse créer vos propres catégories, et les filtre par magasin, carte de paiement ou montant. Mieux encore : la recherche se fait par vrai nom de produit, pas par code de magasin.",
      ],
      sections: [
        {
          heading: "Des catégories automatiques, plus les vôtres",
          body: [
            "Chaque reçu est classé automatiquement dans une catégorie (épicerie, quincaillerie, électronique, vêtements…), sans effort de votre part. Et si votre organisation à vous passe par des catégories comme « Rénos du chalet » ou « École des enfants », créez-les : les catégories personnalisées s'ajoutent aux catégories automatiques.",
          ],
          bullets: [
            "Catégorisation automatique dès la numérisation",
            "Catégories personnalisées illimitées",
            "Une vue claire de vos achats par type",
          ],
        },
        {
          heading: "Filtrer par magasin, par carte de paiement ou par montant",
          body: [
            "« Le reçu de Canadian Tire du mois passé », « ce que j'ai payé avec la carte du travail », « l'achat d'environ 80 $ » : c'est comme ça qu'on se souvient de ses achats. Les filtres de ReFound suivent la même logique.",
          ],
          bullets: [
            "Par magasin : tous vos reçus d'un même marchand",
            "Par carte de paiement : pratique pour séparer les cartes du foyer",
            "Par montant : retrouvez un achat dont vous ne connaissez que l'ordre de grandeur",
          ],
        },
        {
          heading: "La recherche par vrai nom de produit",
          body: [
            "C'est la force distinctive de ReFound. Sur un reçu papier, une perceuse s'appelle « 054-3178-6 ». Comme ReFound [enrichit les articles avec de vrais noms de produits et des images](/fonctionnalites/precision-ocr), vous pouvez chercher « perceuse » et retrouver le bon reçu — même des mois plus tard, même si vous ne savez plus où ni quand vous l'avez achetée.",
            "C'est encore plus vrai quand la ligne imprimée ne distingue rien : sur notre [reçu IKEA de démonstration](/marchands/recus-ikea), trois articles différents apparaissent sous exactement la même ligne « VÅGSJÖN washclth 30x », et sur notre [reçu Home Depot](/marchands/recus-home-depot), la description « XL VT BOT <A> » ne laisse rien deviner du produit acheté.",
            "Aucune recherche dans une galerie de photos ou un dossier de PDF ne permet ça.",
          ],
        },
        {
          heading: "Utile au moment qui compte",
          body: [
            "Retrouver le bon reçu en quelques secondes, c'est exactement ce dont vous avez besoin au comptoir des [retours en magasin](/application-retours-magasin) ou au moment d'une réclamation de garantie. L'organisation n'est pas une fin en soi : c'est ce qui rend vos preuves d'achat utilisables.",
            "La catégorisation et les filtres forment le troisième des [3 critères pour choisir une application de reçus](/blog/comment-choisir-application-recus), avec la [précision OCR](/fonctionnalites/precision-ocr) et le [stockage cloud sécurisé](/fonctionnalites/stockage-cloud-securise).",
          ],
        },
      ],
      faqTitle: "Questions fréquentes sur les catégories et filtres",
      faq: [
        {
          q: "Quelle app permet de filtrer les reçus par magasin ou par carte ?",
          a: "ReFound permet de filtrer vos reçus par magasin, par carte de paiement utilisée et par montant. Vous pouvez aussi chercher par vrai nom de produit, puisque les articles sont enrichis au-delà des codes SKU des magasins.",
        },
        {
          q: "Est-ce que les catégories sont automatiques ?",
          a: "Oui. Chaque reçu est classé automatiquement dans une catégorie dès la numérisation. Vous pouvez aussi créer vos propres catégories personnalisées pour refléter votre organisation.",
        },
        {
          q: "Puis-je créer mes propres catégories ?",
          a: "Oui. Les catégories personnalisées s'ajoutent aux catégories automatiques : « Rénos », « École », « Cadeaux » — celles qui ont du sens pour vous.",
        },
        {
          q: "Comment retrouver un article précis acheté il y a des mois ?",
          a: "Cherchez-le par son vrai nom. Comme ReFound transforme les codes SKU en vrais noms de produits, une recherche comme « perceuse » ou « bottes d'hiver » retrouve le reçu correspondant, même longtemps après l'achat.",
        },
        {
          q: "Puis-je séparer les achats de deux cartes de paiement ?",
          a: "Oui. Le filtre par carte de paiement permet par exemple de distinguer la carte personnelle de celle du travail, ou les cartes de deux conjoints.",
        },
      ],
      ctaTitle: "Le bon reçu, en quelques secondes",
      ctaBody:
        "Catégories automatiques, filtres par magasin, carte ou montant, recherche par vrai nom de produit : avec ReFound, vos reçus sont enfin retrouvables.",
      relatedTitle: "À lire ensuite",
      related: [
        { label: "Comment choisir une application de reçus", href: FR.guide, desc: "Les 3 critères essentiels, avec comparatif." },
        { label: "Précision OCR", href: FR.ocr, desc: "Des reçus lus correctement, du premier coup." },
        { label: "Stockage cloud sécurisé", href: FR.cloud, desc: "Vos reçus survivent à votre téléphone." },
        { label: "Application de reçus", href: FR.receipts, desc: "Scanner, enrichir et retrouver vos reçus." },
      ],
    },
    en: {
      metaTitle: "Categorize and Filter Your Receipts Easily | ReFound",
      metaDescription:
        "ReFound sorts your receipts automatically and filters them by store, payment card or amount. Search by real product name, not by cryptic store SKU codes.",
      keywords:
        "app categorize receipts filters, receipt categories, filter receipts by store, filter receipts by card, search receipt by product, organize receipts, ReFound",
      breadcrumb: "Categories and filters",
      eyebrow: "Feature",
      h1: "Categories and filters: find any receipt in seconds",
      intro: [
        "Keeping receipts is one thing. Finding them is the whole point. A receipt you cannot find at return or claim time is no better than a lost one.",
        "ReFound sorts your receipts automatically, lets you create your own categories, and filters them by store, payment card or amount. Better yet: search works by real product name, not by store code.",
      ],
      sections: [
        {
          heading: "Automatic categories, plus your own",
          body: [
            "Every receipt is automatically sorted into a category (groceries, hardware, electronics, clothing…), with zero effort. And if your own system runs on categories like \"Cottage renos\" or \"Kids' school\", create them: custom categories work alongside the automatic ones.",
          ],
          bullets: [
            "Automatic categorization from the moment you scan",
            "Unlimited custom categories",
            "A clear view of your purchases by type",
          ],
        },
        {
          heading: "Filter by store, payment card or amount",
          body: [
            "\"The Canadian Tire receipt from last month\", \"what I paid with the work card\", \"that purchase around $80\": that is how people actually remember purchases. ReFound's filters follow the same logic.",
          ],
          bullets: [
            "By store: all your receipts from one merchant",
            "By payment card: handy for separating household cards",
            "By amount: find a purchase when you only remember the ballpark",
          ],
        },
        {
          heading: "Search by real product name",
          body: [
            "This is ReFound's strongest differentiator. On a paper receipt, a drill is called \"054-3178-6\". Because ReFound [enriches items with real product names and images](/en/features/ocr-accuracy), you can search \"drill\" and find the right receipt — even months later, even if you no longer remember where or when you bought it.",
            "It matters even more when the printed line tells you nothing: on our [demo IKEA receipt](/en/merchants/ikea-receipts), three different items appear under exactly the same \"VÅGSJÖN washclth 30x\" line, and on our [Home Depot receipt](/en/merchants/home-depot-receipts), the description \"XL VT BOT <A>\" gives away nothing about the product bought.",
            "No camera-roll or PDF-folder search can do that.",
          ],
        },
        {
          heading: "Useful at the moment that counts",
          body: [
            "Finding the right receipt in seconds is exactly what you need at the [in-store return counter](/en/returns-app) or when filing a warranty claim. Organization is not the goal in itself: it is what makes your proof of purchase usable.",
            "Categories and filters are the third of the [3 criteria for choosing a receipt app](/en/blog/how-to-choose-receipt-app), alongside [OCR accuracy](/en/features/ocr-accuracy) and [secure cloud storage](/en/features/secure-cloud-storage).",
          ],
        },
      ],
      faqTitle: "Categories and filters FAQ",
      faq: [
        {
          q: "Which app lets you filter receipts by store or by card?",
          a: "ReFound lets you filter your receipts by store, by payment card used and by amount. You can also search by real product name, since items are enriched beyond the stores' SKU codes.",
        },
        {
          q: "Are categories automatic?",
          a: "Yes. Every receipt is automatically sorted into a category the moment it is scanned. You can also create your own custom categories to match how you organize things.",
        },
        {
          q: "Can I create my own categories?",
          a: "Yes. Custom categories work alongside the automatic ones: \"Renos\", \"School\", \"Gifts\" — whatever makes sense to you.",
        },
        {
          q: "How do I find a specific item bought months ago?",
          a: "Search for it by its real name. Because ReFound turns SKU codes into real product names, a search like \"drill\" or \"winter boots\" finds the matching receipt, even long after the purchase.",
        },
        {
          q: "Can I separate purchases made with two different cards?",
          a: "Yes. The payment card filter lets you tell a personal card from a work card, or two partners' cards, apart.",
        },
      ],
      ctaTitle: "The right receipt, in seconds",
      ctaBody:
        "Automatic categories, filters by store, card or amount, search by real product name: with ReFound, your receipts are finally findable.",
      relatedTitle: "Read next",
      related: [
        { label: "How to choose a receipt app", href: EN.guide, desc: "The 3 key criteria, with a comparison table." },
        { label: "OCR accuracy", href: EN.ocr, desc: "Receipts read correctly, the first time." },
        { label: "Secure cloud storage", href: EN.cloud, desc: "Your receipts outlive your phone." },
        { label: "Receipt app", href: EN.receipts, desc: "Scan, enrich and find your receipts." },
      ],
    },
  },
];

export function getFeature(key: string): PillarPage | undefined {
  return features.find((p) => p.key === key);
}
